'use client';

import Link from 'next/link';

import { useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import type { ClassificationCount, Folder } from '@hub/common/src/types/cit-public-api';

import { getFormattedNumber } from '@hub/common/src/format-utils';
import { addTestThemeToHref, getFolderLink } from '@hub/common/src/navigationUtils';
import { queryFunctions } from '@hub/common/src/query-functions';
import { getContrastColor } from '@hub/common/src/colorUtils';
import { getStandardDerivative } from '@hub/common/src/media-utils';

import { useCitOriginClient } from '@hub/common-components/src/CitOriginClientProvider';

import HtmlRenderer from '@hub/common-components/src/HtmlRenderer';

import ExploreWholeCollectionIcon from './ExploreWholeCollectionIcon';
import ViewAllFoldersIcon from './ViewAllFoldersIcon';
import { useCitThemeClient } from './CitThemeProvider';

export default function FolderGrid({
  folders,
  accountId
}: {
  folders: Folder[];
  accountId: string;
}) {
  const [showAllFolders, setShowAllFolders] = useState(false);

  const citOrigin = useCitOriginClient();

  const { data: account, isLoading: isAccountLoading } = useQuery({
    queryKey: ['account-details', { accountId, citOrigin }],
    queryFn: queryFunctions.getAccountDetails
  });

  const { accountTheme } = useCitThemeClient();

  if (isAccountLoading || !account) {
    return null;
  }

  const bodyFontColor = accountTheme.bodyFontColor();
  const titleFontColor = accountTheme.titleFontColor();

  const fgColor = getContrastColor(bodyFontColor);

  const bodyFont = accountTheme.bodyFont();
  const titleFont = accountTheme.titleFont();

  const lineHeight = accountTheme.lineHeight();
  const letterSpacing = accountTheme.letterSpacing();

  const handleShowAllFolders = () => {
    setShowAllFolders(true);
  };

  return (
    <>
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 py-8'>
        {(showAllFolders ? folders : folders.slice(0, 12)).map((folder: Folder) => (
          <Link
            key={folder.id}
            href={addTestThemeToHref(
              getFolderLink(citOrigin, account.slug || accountId, folder.slug || folder.id),
              accountTheme.isTestTheme() ? accountTheme.getCompressedThemeConfig() : undefined
            )}
            className='group relative flex flex-col'
            prefetch={false}
          >
            <div className='relative aspect-video overflow-hidden rounded-lg'>
              <img
                src={
                  getStandardDerivative(folder.media, {
                    path: accountTheme.backgroundURL(),
                    width: 1000,
                    height: 1000
                  }).path
                }
                alt={folder.name}
                className='w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 aspect-square'
                loading='lazy'
              />
            </div>

            <div className='p-4'>
              <h3
                className='text-xl font-semibold mb-2'
                style={{
                  color: titleFontColor,
                  fontFamily: titleFont,
                  lineHeight,
                  letterSpacing
                }}
              >
                {folder.name}
              </h3>

              {folder.description ? (
                <HtmlRenderer
                  className='text-sm mb-3 line-clamp-2 prose prose-lg prose [&_a]:underline prose-a:text-inherit max-w-none marker:text-inherit'
                  html={folder.description}
                  style={{
                    color: `${bodyFontColor}99`,
                    fontFamily: bodyFont,
                    lineHeight,
                    letterSpacing
                  }}
                />
              ) : null}

              <div
                className='flex items-center text-sm mb-3'
                style={{
                  color: `${bodyFontColor}99`,
                  lineHeight,
                  letterSpacing
                }}
              >
                <span className='font-medium' style={{ lineHeight, letterSpacing }}>
                  {getFormattedNumber(folder.total)}
                </span>
                <span className='ml-1' style={{ fontFamily: bodyFont, lineHeight, letterSpacing }}>
                  items
                </span>
              </div>

              <div className='flex flex-wrap gap-2'>
                {folder.classification?.map((classification: ClassificationCount) => (
                  <span
                    key={classification.classification}
                    className='inline-flex items-center px-2 py-1 rounded-full text-xs font-medium'
                    style={{
                      borderColor: `${bodyFontColor}99`,
                      color: `${bodyFontColor}99`,
                      lineHeight,
                      letterSpacing
                    }}
                  >
                    {classification.classification}
                    <span
                      className='ml-1'
                      style={{
                        color: `${bodyFontColor}66`,
                        fontFamily: bodyFont,
                        lineHeight,
                        letterSpacing
                      }}
                    >
                      {getFormattedNumber(classification.total)}
                    </span>
                  </span>
                ))}
              </div>
            </div>
          </Link>
        ))}
      </div>

      <div className='flex gap-4 justify-center items-center'>
        {!showAllFolders && folders.length > 12 ? (
          <div>
            <button
              className='inline-flex items-center justify-center px-6 py-3 rounded-md text-base font-medium transition-all cursor-pointer'
              style={{
                backgroundColor: bodyFontColor,
                color: fgColor,
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                fontFamily: bodyFont,
                lineHeight,
                letterSpacing
              }}
              onClick={handleShowAllFolders}
            >
              <ViewAllFoldersIcon />
              View All Folders
            </button>
          </div>
        ) : null}
        {/* Explore Whole Collection Button */}
        <div className='flex justify-center py-10'>
          <Link
            href={addTestThemeToHref(
              getFolderLink(citOrigin, account.slug || accountId, undefined),
              accountTheme.isTestTheme() ? accountTheme.getCompressedThemeConfig() : undefined
            )}
            className='inline-flex items-center justify-center px-6 py-3 rounded-md text-base font-medium transition-all'
            style={{
              backgroundColor: bodyFontColor,
              color: fgColor,
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              fontFamily: bodyFont,
              lineHeight,
              letterSpacing
            }}
            prefetch={false}
          >
            <ExploreWholeCollectionIcon />
            Explore Whole Collection
          </Link>
        </div>
      </div>
    </>
  );
}
